import { useMemo } from 'react'
import { InputType, ImageSize } from '@/constants'
import { useSessionStore } from '@/stores'
import Variants from '@/components/steps/Variants'
import SelectActionItems from '@/components/steps/SelectActionItems'
import StepInputArea from '@/components/steps/StepInputArea'
import OrderItems from '@/components/steps/OrderItems'
import InputActionStatus from '@/components/steps/InputActionStatus'
import SelectExchangeProduct from '@/components/steps/SelectExchangeProduct'
import ProcessingGif from '@/components/ProcessingGif'
import OrderTracking from '@/components/Order/OrderTracking'
import { useOmpActions } from '../ompActions'
import { useOmpNav } from '../hooks/useOmpNav'
import { useOrderItems } from '../hooks/usePortalData'
import { buildItemActionButtons, deriveActionStepKind, exchangeProductInfo } from '../utils/portalActions'
import { handleProductImageError, resolveDeliveryLine, resolveProductImage } from '../utils/omp'
import { getThemeVariables } from '../utils/themeTokens'
import OmpDrawer, { type OmpDrawerSize } from './OmpDrawer'
import AddProductDrawer from './AddProductDrawer'
import Checkout from '@/components/steps/Checkout'
import OmpPaymentUpdateDrawer from './OmpPaymentUpdateDrawer'
import OmpButton from '../components/OmpButton'
import OmpStepBanner from '../components/OmpStepBanner'

// Modal width tier per intent (stable from the moment the action starts — no resize mid-flow).
// Backend step content needs room to breathe, so `lg` is the floor for every action.
const WIDE_INTENTS = [InputType.ShipmentTracking, InputType.ItemStatus, InputType.RebillItem]

function modalSizeForIntent(intent: number | null): OmpDrawerSize {
  if (intent != null && WIDE_INTENTS.includes(intent)) {
    return 'wide'
  }
  return 'lg'
}

// Pull the variants field the backend attached to the current action step, and
// map its predetermined replacement product into the shape <Variants> expects.
function variantsContext(session: any): { field: any; newCampaignProduct: any } | null {
  const inputs = [...((session?.valid_inputs as any[]) || []), ...((session?.additional_inputs as any[]) || [])]
  for (const input of inputs) {
    const field = input?.input_fields?.variants
    if (field) {
      const rp = field.replacement_product
      const newCampaignProduct = rp
        ? {
            id: rp.campaign_product_id,
            show_variants: rp.show_variants,
            bundle_quantity: rp.bundle_quantity,
            product: { id: rp.product_id, variant_type: rp.variant_type },
          }
        : null
      return { field, newCampaignProduct }
    }
  }
  return null
}

const OmpActionRouter = () => {
  const {
    mode,
    title,
    item,
    product,
    busy,
    error,
    retry,
    intent,
    exchangeProduct,
    submitInput,
    submitData,
    submitCheckout,
    cancel,
    close,
  } = useOmpActions()
  const { navigateWithInput } = useOmpNav()
  const campaign = useSessionStore(s => s.campaign)
  const storeSession = useSessionStore(s => s.session) as any
  const popupSession = useSessionStore(s => s.popupSession) as any
  // A centred modal gets the width for checkout's two-column layout; a side drawer stacks.
  const centeredLayout = getThemeVariables(campaign)?.popup_layout === 'center'
  // Action steps come back as popups: the live step lands in popupSession while
  // state.session holds the portal step behind it. Render the live one.
  const session = popupSession ?? storeSession
  const customer = session?.customer ?? storeSession?.customer

  // View Status carousels through the whole order, not just the item that opened it.
  // OrderItems picks the starting slide from session.selected_item. The id is stringified so
  // the key matches the details page's own fetch and resolves from cache.
  const statusOrderId = intent === InputType.ItemStatus && item?.order?.id != null ? String(item.order.id) : null
  const { data: orderItems, isLoading: statusItemsLoading } = useOrderItems(
    { order: statusOrderId ?? undefined },
    statusOrderId != null
  )

  // Only the order's other View Status items — the button is per item, so the carousel
  // covers the items that offer it, not everything in the order. Built off the portal step
  // (storeSession), which is where the intents allow-list lives while a popup is open.
  const statusItems = useMemo(() => {
    const all = orderItems?.length ? orderItems : item ? [item] : []
    if (all.length < 2) {
      return all
    }
    const offersStatus = all.filter(candidate =>
      buildItemActionButtons(candidate, storeSession, campaign, undefined).some(b => b.intent === InputType.ItemStatus)
    )
    return offersStatus.length ? offersStatus : all
  }, [orderItems, item, storeSession, campaign])

  const renderBody = () => {
    if (mode === 'add_product') {
      return (
        <AddProductDrawer
          product={product}
          deliveryLine={resolveDeliveryLine(customer)}
          onClose={close}
          onEditAddress={() => {
            close()
            navigateWithInput('/portal/account/addresses')
          }}
        />
      )
    }

    if (mode === 'checkout') {
      const checkoutInput = (session?.valid_inputs as any[])?.[0]
      if (!checkoutInput) {
        return null
      }
      return (
        <Checkout
          compact
          singleColumn={!centeredLayout}
          resolveImage={() => (item ? resolveProductImage(item, false, ImageSize.Medium) : undefined)}
          onImageError={e => item && handleProductImageError(e, item)}
          session={session}
          onSubmit={(input, params) => submitCheckout(input ?? checkoutInput, params)}
          onLeave={cancel}
        />
      )
    }

    // Lifecycle action — render whatever step the backend returned.
    const kind = deriveActionStepKind(session, !!exchangeProduct)

    // Own the whole action so a confirm step in the rebill path can't surface raw.
    if (kind === 'paymentUpdate' || (intent === InputType.RebillItem && kind !== 'done')) {
      return (
        <OmpPaymentUpdateDrawer
          session={session}
          item={item ?? session?.selected_item}
          error={error}
          onSubmit={(inp, data) => submitInput(inp, data)}
        />
      )
    }

    // A failed submit must not re-render the step: auto-submitting steps
    // (auto_select_single_item) fire again on remount and loop on the same error.
    if (error) {
      return (
        <div className="omp-action-error">
          <p className="omp-action-error-text">{error}</p>
          <div className="omp-action-footer">
            <OmpButton variant="primary" fullWidth onClick={retry}>
              Try Again
            </OmpButton>
            <button type="button" className="omp-link-underline" onClick={cancel}>
              Close
            </button>
          </div>
        </div>
      )
    }

    if (kind === 'variants') {
      const vc = variantsContext(session)
      const target = item || session?.selected_item
      return (
        <Variants
          item={target}
          newCampaignProduct={vc?.newCampaignProduct ?? exchangeProduct}
          quantity={target?.quantity}
          subscriptionOnly={!!vc?.field?.subscription_only}
          session={session}
          onSelect={data => submitData(data)}
          onBack={cancel}
        />
      )
    }
    if (kind === 'rmaItems') {
      return (
        <SelectActionItems
          label="Submit"
          session={session}
          onSelect={(data, mainItem) => submitData(data, mainItem)}
          onBack={cancel}
        />
      )
    }
    if (kind === 'tracking') {
      return (
        <OrderTracking
          item={item ?? session?.selected_item}
          fulfillments={session?.fulfillments}
          session={session}
          onClear={cancel}
        />
      )
    }
    if (kind === 'itemStatus') {
      const input = session?.valid_inputs?.[0]
      const target = item ?? session?.selected_item
      // OrderItems reads `items` once on mount (Vue's created() does the same), so it must
      // not mount before the order's items land — and it remounts if the set changes.
      if (statusOrderId && statusItemsLoading) {
        return (
          <div className="omp-drawer-loading-overlay">
            <ProcessingGif processing />
          </div>
        )
      }
      const items = statusItems.length ? statusItems : target ? [target] : []
      return (
        <OrderItems
          key={items.map(i => i.id).join('-')}
          input={input}
          items={items}
          session={session}
          onSubmit={inp => submitInput(inp)}
          onBack={cancel}
          onEdit={editInput => submitInput(editInput)}
        />
      )
    }
    if (kind === 'inputActionStatus') {
      const input = session?.valid_inputs?.[0]
      return (
        <InputActionStatus
          input={input}
          session={session}
          hideClose
          onDone={(inp: any) => submitInput(inp ?? input)}
          onBack={cancel}
        />
      )
    }
    if (kind === 'exchangeProduct') {
      const info = exchangeProductInfo(session)
      return (
        <SelectExchangeProduct
          item={item ?? session?.selected_item}
          subscriptionOnly={!!info?.subscriptionOnly}
          quantityType={info?.quantityType}
          currentSession={session}
          onSelect={(inp: any, data?: any, campaignProduct?: any) => submitInput(inp, data, campaignProduct)}
          onBack={cancel}
        />
      )
    }
    if (kind === 'step') {
      // The funnel wraps every popup step in .content-confirmation (SessionContainer);
      // the whole popup rule set — centred button rows, uncapped choice buttons, title
      // spacing — hangs off it. A drawer-hosted step is the same context, so it opts in
      // too rather than re-deriving each rule under an OMP selector.
      return (
        <div className="content-confirmation">
          <StepInputArea session={session} onSelectInput={(input, data) => submitInput(input, data)} onBack={cancel} />
        </div>
      )
    }
    return null
  }

  return (
    <OmpDrawer
      isOpen={mode !== null}
      onClose={cancel}
      closeDisabled={busy}
      title={title}
      size={mode === 'add_product' ? 'lg' : mode === 'checkout' ? 'wide' : modalSizeForIntent(intent)}
      forceCentered={intent === InputType.ReactivateSubscription}
      overlay={
        /* Overlay, never a swap: replacing the body unmounts the step, and an
           auto_select_single_item step re-submits the moment it remounts. */
        busy ? (
          <div className="omp-drawer-loading-overlay">
            <ProcessingGif processing />
          </div>
        ) : null
      }>
      <OmpStepBanner session={session} />
      {renderBody()}
    </OmpDrawer>
  )
}

export default OmpActionRouter
